import { useMemo, useState } from 'react';
import type {
  AdminTranscriptCommitResponse,
  AdminTranscriptPreviewResponse,
  ReviewAsset,
  TranscriptAssetProposal,
} from '../../../shared/types';
import Chip from '../Chip';
import Eyebrow from '../Eyebrow';
import ProgressTracker from '../ProgressTracker';
import { useModalFocusTrap } from '../useModalFocusTrap';
import { adminApi, ApiError } from '../../lib/api';
import {
  FieldLabel,
  StatePanel,
  cardClass,
  inputClass,
  primaryButtonGlowClass,
  quietButtonClass,
  secondaryButtonClass,
} from './AdminUi';

type Stage = 'file' | 'preview' | 'review' | 'result';

const importStages: Array<{ value: Stage; label: string }> = [
  { value: 'file', label: 'File' },
  { value: 'preview', label: 'Preview' },
  { value: 'review', label: 'Review' },
  { value: 'result', label: 'Result' },
];

interface ProposalDraft extends TranscriptAssetProposal {
  speaker_label: string;
  existing_asset_id: string | null;
  skipped: boolean;
}

export default function AdminTranscriptImport({
  projectId,
  assets,
  onClose,
  onImported,
}: {
  projectId: string;
  assets: ReviewAsset[];
  onClose: () => void;
  onImported: (assetIds: string[]) => void;
}) {
  const [stage, setStage] = useState<Stage>('file');
  const [filename, setFilename] = useState('');
  const [content, setContent] = useState('');
  const [preview, setPreview] = useState<AdminTranscriptPreviewResponse | null>(null);
  const [fps, setFps] = useState(25);
  const [proposals, setProposals] = useState<ProposalDraft[]>([]);
  const [result, setResult] = useState<AdminTranscriptCommitResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [replacementLocked, setReplacementLocked] = useState(false);

  const hasBlockingIssues = useMemo(
    () => preview?.issues.some((issue) => issue.severity === 'error') ?? false,
    [preview],
  );
  const activeStageIndex = importStages.findIndex((item) => item.value === stage);
  const panelRef = useModalFocusTrap<HTMLElement>(onClose);

  async function chooseFile(file: File | undefined) {
    if (!file) return;
    setFilename(file.name);
    setContent(await file.text());
    setError(null);
    setUnavailable(false);
  }

  async function runPreview() {
    if (!filename || !content) return;
    setBusy(true);
    setError(null);
    setUnavailable(false);
    try {
      const next = await adminApi.previewTranscript({ filename, content });
      setPreview(next);
      setFps(next.guessed_fps ?? 25);
      setProposals(
        next.sections.map((section) => ({
          section_index: section.section_index,
          offset_s: section.first_cue_s ?? 0,
          title: section.heading || `Section ${section.section_index + 1}`,
          speaker_label: suggestedSpeakerLabel(section.segments),
          existing_asset_id: null,
          skipped: false,
        })),
      );
      setStage('preview');
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 501) setUnavailable(true);
      else setError(caught instanceof ApiError ? caught.message : 'We could not preview that transcript. Mind checking the file?');
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if (!preview) return;
    const activeProposals = proposals.filter((proposal) => !proposal.skipped);
    if (activeProposals.length === 0) {
      setError('Keep at least one section to import.');
      return;
    }
    if (activeProposals.some((proposal) => !proposal.title.trim())) {
      setError('Give every proposed asset a title.');
      return;
    }
    const existingAssetIds = activeProposals
      .map((proposal) => proposal.existing_asset_id)
      .filter((assetId): assetId is string => assetId !== null);
    if (new Set(existingAssetIds).size !== existingAssetIds.length) {
      setError('Each asset can only be mapped once in an import.');
      return;
    }
    setBusy(true);
    setError(null);
    setUnavailable(false);
    setReplacementLocked(false);
    try {
      const next = await adminApi.commitTranscript({
        filename,
        content,
        source_format: preview.source_format,
        fps,
        mappings: [],
        project_id: projectId,
        asset_proposals: activeProposals.map(({ skipped: _skipped, ...proposal }) => ({
          ...proposal,
          title: proposal.title.trim(),
          speaker_label: proposal.speaker_label.trim() || null,
        })),
      });
      setResult(next);
      setStage('result');
      onImported(next.imports.map((item) => item.asset_id));
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 501) setUnavailable(true);
      else if (caught instanceof ApiError && caught.status === 409) setReplacementLocked(true);
      else setError(caught instanceof ApiError ? caught.message : 'We could not import the transcript. Mind trying again?');
    } finally {
      setBusy(false);
    }
  }

  function updateProposal(index: number, updates: Partial<ProposalDraft>) {
    setProposals((current) => current.map((proposal, proposalIndex) =>
      proposalIndex === index ? { ...proposal, ...updates } : proposal,
    ));
  }

  return (
    <div className="app-scrim fade-in fixed inset-0 z-50 grid place-items-center overflow-y-auto p-5" role="presentation">
    <section ref={panelRef} className={`${cardClass} scale-in my-8 max-h-[calc(100vh-3rem)] w-full max-w-xl overflow-y-auto p-8 shadow-[var(--shadow-ambient-lg)]`} role="dialog" aria-modal="true" aria-labelledby="transcript-import-title" tabIndex={-1}>
      <Eyebrow accent>Import transcript</Eyebrow>
      <ProgressTracker
        className="mt-6"
        ariaLabel="Transcript import steps"
        steps={importStages.map((item, index) => ({
          label: item.label,
          state: index < activeStageIndex ? 'done' : index === activeStageIndex ? 'active' : 'pending',
        }))}
      />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 id="transcript-import-title" className="title-lg mt-7">
            {stage === 'file'
              ? 'Choose a transcript'
              : stage === 'preview'
                ? 'Check the transcript'
                : stage === 'review'
                  ? 'Review proposed assets'
                  : 'Import complete'}
          </h2>
        </div>
        <button type="button" className={quietButtonClass} onClick={onClose}>Close</button>
      </div>
      <p className="mt-2 text-sm text-muted">
        {stage === 'file'
          ? 'Choose a Resolve TXT, SRT, or VTT file to begin.'
          : stage === 'preview'
            ? 'We read the file and split it into interview sections. Check the frame rate before continuing.'
            : stage === 'review'
              ? 'Check how each section will become an interview asset.'
              : 'The transcript and its interview sections are ready.'}
      </p>

      {unavailable ? (
        <div className="mt-6">
          <StatePanel eyebrow="Not available yet" title="Transcript import is waiting for Phase 1A" tone="tint">
            <p>The preview and commit endpoints have not been merged into this worktree yet. Your selected file has not been uploaded or changed.</p>
          </StatePanel>
        </div>
      ) : null}
      {replacementLocked ? (
        <div className="mt-6">
          <StatePanel eyebrow="Replacement locked" title="This transcript already has client decisions" tone="error">
            <p>We have kept the current transcript in place so existing selections stay attached to the right words. Create a new asset if you need a separate transcript.</p>
          </StatePanel>
        </div>
      ) : null}
      {error ? <p className="mt-6 rounded-input bg-error-tint p-3 text-sm text-error" role="alert">{error}</p> : null}

      {stage === 'file' ? (
        <div className="mt-7">
          <label className="block">
            <FieldLabel>Resolve TXT, SRT, or VTT file</FieldLabel>
            <input
              className={`${inputClass} file:mr-4 file:rounded-full file:border-0 file:bg-accent-tint file:px-4 file:py-2 file:font-semibold file:text-accent`}
              type="file"
              accept=".txt,.srt,.vtt,text/plain,text/vtt,application/x-subrip"
              onChange={(event) => void chooseFile(event.target.files?.[0])}
            />
          </label>
          {filename ? <p className="mt-3 text-sm text-muted">Ready to preview: {filename}</p> : null}
          <button type="button" className={`${primaryButtonGlowClass} mt-6`} onClick={() => void runPreview()} disabled={busy || !content}>
            {busy ? 'Preparing preview' : 'Preview transcript'}
          </button>
        </div>
      ) : null}

      {stage === 'preview' && preview ? (
        <div className="mt-7 space-y-6">
          <div className="grid gap-4 sm:grid-cols-[10rem_1fr]">
            <label>
              <FieldLabel>Frame rate</FieldLabel>
              <select className={inputClass} value={fps} onChange={(event) => setFps(Number(event.target.value))}>
                {[23.976, 24, 25, 29.97, 30, 50, 59.94, 60].map((value) => <option key={value} value={value}>{value} fps</option>)}
              </select>
            </label>
            <div className="rounded-input border border-line bg-elevated p-4 text-sm text-muted">
              <p><strong className="text-fg">Detected format:</strong> {preview.source_format.replace('_', ' ')}</p>
              <p className="mt-1"><strong className="text-fg">Frame guess:</strong> {preview.guessed_fps ? `${preview.guessed_fps} fps` : 'No confident guess'}</p>
            </div>
          </div>

          <section>
            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-faint">Sections</p>
            <ul className="mt-3 overflow-hidden rounded-input border border-line">
              {preview.sections.map((section) => (
                <li key={section.section_index} className="border-t border-line bg-card p-4 first:border-t-0">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="font-display text-[0.98rem] font-semibold">{section.heading || `Section ${section.section_index + 1}`}</h3>
                    <Chip>{section.segments.length} cues</Chip>
                  </div>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.1em] text-muted">
                    {secondsLabel(section.first_cue_s)} to {secondsLabel(section.last_cue_s)}
                  </p>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-faint">Warnings</p>
            {preview.issues.length === 0 ? (
              <p className="mt-3 flex items-center gap-3 rounded-input bg-success-tint p-4 text-sm text-success"><Chip tone="success" dot>All clear</Chip>No transcript warnings found.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {preview.issues.map((issue, index) => (
                  <li key={`${issue.code}-${index}`} className={`flex items-start gap-3 rounded-input p-4 text-sm ${issue.severity === 'error' ? 'bg-error-tint text-error' : 'bg-warning-tint text-warning'}`}>
                    <Chip tone={issue.severity === 'error' ? 'error' : 'warning'} dot>{issue.severity === 'error' ? 'Needs attention' : 'Check this'}</Chip>
                    <span>{issue.message}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <div className="flex flex-wrap justify-end gap-3">
            <button type="button" className={quietButtonClass} onClick={() => setStage('file')}>Choose another file</button>
            <button type="button" className={primaryButtonGlowClass} onClick={() => setStage('review')} disabled={hasBlockingIssues}>
              Review proposed assets
            </button>
          </div>
        </div>
      ) : null}

      {stage === 'review' && preview ? (
        <div className="mt-7">
          <p className="max-w-3xl text-sm text-muted">Each transcript section will create an asset. Adjust its details, attach it to an existing asset, or skip it.</p>
          <div className="mt-5 grid gap-4">
            {preview.sections.map((section, index) => {
              const proposal = proposals[index];
              if (!proposal) return null;
              const action = proposal.skipped
                ? 'skip'
                : proposal.existing_asset_id ?? 'create';
              const createsAsset = !proposal.skipped && proposal.existing_asset_id === null;
              return (
                <section key={section.section_index} className="rounded-card border border-line bg-page p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-xl">{section.heading || `Section ${section.section_index + 1}`}</h3>
                    <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">{section.segments.length} cues</span>
                  </div>
                  <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <label>
                      <FieldLabel>Asset action</FieldLabel>
                      <select
                        className={inputClass}
                        value={action}
                        onChange={(event) => {
                          const value = event.target.value;
                          updateProposal(index, {
                            skipped: value === 'skip',
                            existing_asset_id: value === 'create' || value === 'skip' ? null : value,
                          });
                        }}
                      >
                        <option value="create">Create a new asset</option>
                        {assets.map((asset) => <option key={asset.id} value={asset.id}>Use {asset.title}</option>)}
                        <option value="skip">Skip this section</option>
                      </select>
                    </label>
                    <label>
                      <FieldLabel>Asset title</FieldLabel>
                      <input
                        className={inputClass}
                        value={proposal.title}
                        onChange={(event) => updateProposal(index, { title: event.target.value })}
                        disabled={!createsAsset}
                        required={createsAsset}
                      />
                    </label>
                    <label>
                      <FieldLabel>Speaker label</FieldLabel>
                      <input
                        className={inputClass}
                        value={proposal.speaker_label}
                        onChange={(event) => updateProposal(index, { speaker_label: event.target.value })}
                        disabled={!createsAsset}
                        placeholder="Not set"
                      />
                    </label>
                    <label>
                      <FieldLabel>Offset in seconds</FieldLabel>
                      <input
                        className={inputClass}
                        type="number"
                        step="0.001"
                        value={proposal.offset_s}
                        onChange={(event) => updateProposal(index, { offset_s: Number(event.target.value) })}
                        disabled={proposal.skipped}
                      />
                    </label>
                  </div>
                  {proposal.skipped ? <p className="mt-3 text-sm text-muted">This section will not be imported.</p> : null}
                </section>
              );
            })}
          </div>
          <div className="mt-7 flex flex-wrap justify-end gap-3">
            <button type="button" className={secondaryButtonClass} onClick={() => setStage('preview')}>Back to preview</button>
            <button type="button" className={primaryButtonGlowClass} onClick={() => void commit()} disabled={busy}>
              {busy ? 'Importing transcript' : 'Import transcript'}
            </button>
          </div>
        </div>
      ) : null}

      {stage === 'result' && result ? (
        <div className="mt-7">
          <ul className="grid gap-3">
            {result.imports.map((item) => {
              const asset = item.asset ?? assets.find((candidate) => candidate.id === item.asset_id);
              return (
                <li key={item.asset_id} className="rounded-input bg-success-tint p-4 text-success">
                  <strong>{asset?.title ?? item.asset_id}</strong>. Imported {item.segment_count} transcript cues as version {item.transcript_version.version}.
                </li>
              );
            })}
          </ul>
          <button type="button" className={`${primaryButtonGlowClass} mt-6`} onClick={onClose}>Done</button>
        </div>
      ) : null}
    </section>
    </div>
  );
}

function secondsLabel(value: number | null): string {
  if (value === null) return 'No timecode';
  const totalFrames = Math.max(0, Math.round(value * 1000));
  const hours = Math.floor(totalFrames / 3_600_000);
  const minutes = Math.floor((totalFrames % 3_600_000) / 60_000);
  const seconds = Math.floor((totalFrames % 60_000) / 1_000);
  const millis = totalFrames % 1_000;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

function suggestedSpeakerLabel(
  segments: AdminTranscriptPreviewResponse['sections'][number]['segments'],
): string {
  const counts = new Map<string, { count: number; firstIndex: number }>();
  for (const [index, segment] of segments.entries()) {
    const label = segment.speaker_label?.trim();
    if (!label) continue;
    const current = counts.get(label);
    counts.set(label, current
      ? { ...current, count: current.count + 1 }
      : { count: 1, firstIndex: index });
  }
  return [...counts.entries()]
    .sort((left, right) => right[1].count - left[1].count || left[1].firstIndex - right[1].firstIndex)[0]?.[0] ?? '';
}
